import { mtFetch, type FetchResult, type QueryParams } from "./mtFetch";

// ---------------------------------------------------------------------------
// Типы — зеркалят canonical JSON, который возвращает backend.
// ---------------------------------------------------------------------------

export type LlmProvider = "openai" | "anthropic" | "deepseek" | "routerai";
export type LlmRouteName =
  | "openai_direct"
  | "anthropic_direct"
  | "deepseek_direct"
  | "routerai";
export type LlmSuggestionMode = "realtime" | "batch";
export type LlmSuggestionStatus = "pending" | "completed" | "failed" | "cancelled";

export interface LlmRoute {
  name: LlmRouteName;
  upstream_model: string;
  supports_batch: boolean;
  supports_strict_schema: boolean;
}

export interface LlmModelEntry {
  model_id: string;
  label: string;
  family: LlmProvider | "other";
  supports_strict_schema: boolean;
  notes: string;
  routes: LlmRoute[];
}

export interface LlmRoutePreview {
  model_id: string;
  route?: LlmRouteName;
  upstream_model?: string;
}

export interface LlmRecommendation {
  rule_name: string;
  reason: string;
  advisor: LlmRoutePreview;
  judge: LlmRoutePreview;
}

export interface LlmConfigPayload {
  schema_version: string;
  prompt_version: string;
  models: LlmModelEntry[];
  recommendation: LlmRecommendation;
}

export interface LlmAdvisorAlternative {
  index: number;
  target_text: string;
  reasoning: string;
  confidence: number;
  warnings: string[];
}

export interface LlmAdvisorSummary {
  best_alternative_index: number | null;
  consensus_confidence: number | null;
  abstain: boolean;
  abstain_reason: string | null;
}

export interface LlmAdvisorPayload {
  alternatives: LlmAdvisorAlternative[];
  summary: LlmAdvisorSummary;
}

export interface LlmJudgeScore {
  alternative_index: number;
  score: number;
  rationale: string;
}

export interface LlmJudgePayload {
  winner_index: number | null;
  scores: LlmJudgeScore[];
  abstain: boolean;
}

export interface LlmSuggestion {
  suggestion_id: string;
  review_item_id: string;
  stable_key: string;
  batch_job_id: string | null;
  mode: LlmSuggestionMode;
  provider: LlmProvider;
  model: string;
  judge_provider: LlmProvider | null;
  judge_model: string | null;
  prompt_version: string;
  requested_by: string;
  status: LlmSuggestionStatus;
  advisor_payload: LlmAdvisorPayload | Record<string, never>;
  judge_payload: LlmJudgePayload | null;
  applied_decision_id: string | null;
  applied_alternative_index: number | null;
  raw_response: unknown;
  usage: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface SuggestRequest {
  review_item_id?: string;
  stable_key?: string;
  model_id: string;
  route?: LlmRouteName;
  n_alternatives?: number;
  with_judge?: boolean;
  judge_model_id?: string;
  judge_route?: LlmRouteName;
  extra_context?: Record<string, unknown>;
}

export interface SuggestResponse {
  schema_version: string;
  suggestion: LlmSuggestion;
}

export interface LlmSuggestionsList {
  schema_version: string;
  review_item_id: string;
  suggestions: LlmSuggestion[];
}

// ---------------------------------------------------------------------------
// Batch (Phase C)
// ---------------------------------------------------------------------------

export type LlmBatchStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "failed"
  | "expired"
  | "cancelled";

export interface LlmBatchJob {
  batch_job_id: string;
  provider_batch_id: string | null;
  provider: LlmProvider;
  model: string;
  judge_provider: LlmProvider | null;
  judge_model: string | null;
  requested_by: string;
  item_count: number;
  status: LlmBatchStatus;
  output_ref: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface BatchSubmitRequest {
  review_item_ids: string[];
  model_id: string;
  route?: LlmRouteName;
  n_alternatives?: number;
  judge_model_id?: string;
  extra_context?: Record<string, unknown>;
}

export interface BatchSubmitResponse {
  schema_version: string;
  batch_job: LlmBatchJob;
  pending_suggestion_ids: string[];
}

export interface LlmBatchJobsList {
  schema_version: string;
  batch_jobs: LlmBatchJob[];
}

export interface LlmBatchJobDetail {
  schema_version: string;
  batch_job: LlmBatchJob;
  suggestions: LlmSuggestion[];
}

export interface ListBatchJobsFilters {
  requested_by?: string;
  status?: LlmBatchStatus;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// API-обёртки.
// ---------------------------------------------------------------------------

export function getLlmConfig(
  reviewItemId?: string,
): Promise<FetchResult<LlmConfigPayload>> {
  return mtFetch<LlmConfigPayload>("/admin/llm-config", {
    query: reviewItemId ? { review_item_id: reviewItemId } : undefined,
  });
}

export function requestLlmSuggestion(
  body: SuggestRequest,
): Promise<FetchResult<SuggestResponse>> {
  return mtFetch<SuggestResponse>("/admin/llm-suggest", {
    method: "POST",
    body,
  });
}

export function listLlmSuggestions(
  reviewItemId: string,
  limit = 20,
): Promise<FetchResult<LlmSuggestionsList>> {
  return mtFetch<LlmSuggestionsList>("/admin/llm-suggestions", {
    query: { review_item_id: reviewItemId, limit },
  });
}

export function submitLlmBatch(
  body: BatchSubmitRequest,
): Promise<FetchResult<BatchSubmitResponse>> {
  return mtFetch<BatchSubmitResponse>("/admin/llm-suggest/batch", {
    method: "POST",
    body,
  });
}

export function listLlmBatchJobs(
  filters: ListBatchJobsFilters = {},
): Promise<FetchResult<LlmBatchJobsList>> {
  return mtFetch<LlmBatchJobsList>("/admin/llm-batch-jobs", {
    query: filters as QueryParams,
  });
}

export function getLlmBatchJob(
  batchJobId: string,
  suggestionLimit = 200,
): Promise<FetchResult<LlmBatchJobDetail>> {
  return mtFetch<LlmBatchJobDetail>(
    `/admin/llm-batch-jobs/${encodeURIComponent(batchJobId)}`,
    { query: { suggestion_limit: suggestionLimit } },
  );
}
